(ns graph-ui.web.your-work
  "The widget kinds :your-work-group and :in-progress-list
   (IKC3/PLAN-ikc-v3-080-01/ME, screens 8 and 9 of the plan). They are
   declared in dsl/contexts/ui/meta-ui/v2.1-SNAPSHOT/your-work-group.edn and
   in-progress-list.edn, and the declarations hold the reasons. This
   namespace draws them.

   ONE GROUP OF YOUR WORK: a heading with the count, then the rows. Each act
   sends ONE gesture :act with a map that the widget composes. Decide… and
   Pick a time… open a field under the row first. Reopen… opens the Reopen
   dialog of the tracker (graph-ui.web.reopen), which sends :workflow/reopen.
   The commands of the workflow decide, and a refusal is the sentence of the
   status line of the surface.

   THE OPEN FIELD IS A STATE OF THE BROWSER, an atom of the group. It keeps
   the id of the row and the text, and it closes when the row leaves.

   EVERY CHILD LIST IS SPREAD WITH `into`. A bare vector of hiccup as one
   child is read by Reagent as a component call (CLAUDE.md)."
  (:require [clojure.string :as str]
            [graph-ui.web.backlog :as backlog]
            [graph-ui.web.bind :as bind]
            [graph-ui.web.reopen :as reopen]
            [reagent.core :as r]))

;; @plan PLAN-ikc-v3-080-01-the-tracker-v2
;; @adr ADR-graph-ui-v0-013-01-one-layout-model

(defn- fire!
  "Dispatch the command of `template` with `value` as $value."
  [bus session-id template value]
  (when (and bus template)
    (js-invoke bus "dispatch"
               (bind/fill-command template {'$session-id session-id '$value value})
               (fn [_] nil))))

(def ^:private muted "text-[var(--muted)]")
(def ^:private small-button
  (str "rounded border border-[var(--border)] px-2 py-0.5 text-xs whitespace-nowrap "
       "text-[var(--text)] bg-[var(--panel)] hover:bg-[var(--hover)]"))
(def ^:private primary-button
  "rounded px-2 py-0.5 text-xs whitespace-nowrap bg-[var(--accent)] text-white")

(def action-labels
  "The words of each act of a row."
  {"decide" "Decide…" "accept" "Accept" "reopen" "Reopen…" "done" "Done" "pick-time" "Pick a time…"})

(defn- mark [kind]
  (let [[glyph colour] (get backlog/kind-marks kind ["•" "var(--muted)"])]
    [:span {:class "inline-block w-4 text-center shrink-0" :style {:color colour} :title kind} glyph]))

(defn- key-link [k]
  [:a {:class "font-mono text-xs font-semibold shrink-0 hover:underline"
       :style {:minWidth "5.5em"}
       :href (when (seq k) (str "/browse/" k))}
   (or k "—")])

(defn- open-field
  "The field under a row: the options of a Decision as buttons, a text and
   the button that sends."
  [{:keys [id options recommendation]} action text bus session-id act close!]
  (let [send! (fn [v] (when-not (str/blank? (str v))
                        (fire! bus session-id act {:item-id id :action action :value v})
                        (close!)))]
    (into [:div {:class "flex flex-wrap items-center gap-2 px-3 py-2 border-t border-[var(--border)] text-sm"
                 :style {:paddingLeft "2.5rem"}
                 :data-open-field action}]
          (concat
           (when (and (= "decide" action) (seq recommendation))
             [[:span {:class (str "text-xs " muted)} (str "Recommended: " recommendation)]])
           (when (= "decide" action)
             (for [o options]
               ^{:key o}
               [:button {:class small-button :data-option o :on-click #(send! o)} o]))
           [[:input {:class "rounded border border-[var(--border)] px-2 py-0.5 text-sm bg-[var(--panel)] text-[var(--text)]"
                     :style {:minWidth "16rem" :flex "1 1 16rem"}
                     :data-answer action
                     :placeholder (if (= "decide" action) "Your answer, in your words" "The time, for example Wed 15 Oct 10:00")
                     :value @text :auto-focus true
                     :on-change #(reset! text (.. % -target -value))
                     :on-key-down #(when (= "Enter" (.-key %)) (send! @text))}]
            [:button {:class primary-button :data-send action :on-click #(send! @text)}
             (if (= "decide" action) "Decide" "Send")]
            [:button {:class small-button :on-click close!} "Cancel"]]))))

(defn- row-view
  [{:keys [id key kind title context age status actions] :as row} open text bus session-id act]
  (let [open? (= id (:id @open))]
    [:div {:class "flex flex-col" :data-row id}
     (into
      ;; THE ROW WRAPS ON A NARROW WINDOW, so the title keeps its width and
      ;; the context and the buttons go to a second line (probe of
      ;; 2026-10-17, me-5-your-work-narrow.png: the title was cut to nothing).
      ;; THE TITLE IS NEVER CUT (IKC-317, George, 2026-11-01). It wraps to
      ;; the lines it needs, as the title of a backlog row does.
      [:div {:class "flex flex-wrap items-center gap-2 px-3 py-1.5 border-t border-[var(--border)] text-sm min-w-0"}]
      (concat
       [[mark kind]
        [key-link key]
        [:span {:class "flex-1 break-words" :style {:minWidth "12rem"} :title title} title]
        (when (seq context)
          [:span {:class (str "text-xs truncate " muted) :style {:maxWidth "40%"} :title context} context])
        (when (seq age) [:span {:class (str "text-xs shrink-0 " muted) :style {:minWidth "4.5em" :textAlign "right"}} age])
        (when (seq status)
          [:span {:class "text-[10px] font-semibold uppercase rounded bg-[var(--hover)] px-1 shrink-0"} status])]
       (for [a actions]
         ^{:key a}
         [:button {:class (if (#{"decide" "accept" "done" "pick-time"} a) primary-button small-button)
                   :data-action a
                   :on-click (fn [_]
                               (case a
                                 "reopen"             (reopen/open! {:bus bus :item-id (uuid id)})
                                 ("decide" "pick-time") (do (reset! text "") (reset! open {:id id :action a}))
                                 (fire! bus session-id act {:item-id id :action a})))}
          (get action-labels a a)])))
     (when open?
       [open-field row (:action @open) text bus session-id act #(reset! open nil)])]))

(defn widget-your-work-group
  "One group of Your work. Gesture :act."
  [_props _bus _session-id]
  (let [open (r/atom nil)
        text (r/atom "")]
    (fn [{:keys [value empty-label] :as props} bus session-id]
      (let [{:keys [label says rows]} value
            act  (:act (:on-gesture props))
            ids  (set (map :id rows))
            _    (when (and @open (not (ids (:id @open)))) (reset! open nil))]
        (when (and value (or (seq rows) empty-label))
          [:div {:class "flex flex-col rounded bg-[var(--panel)] overflow-hidden border border-[var(--border)]"
                 :data-group (:id value "")}
           [:div {:class "flex items-baseline gap-2 px-3 py-2"}
            [:span {:class "font-semibold text-[var(--heading)]"} (str label " · " (:count value (count rows)))]
            (when (seq says) [:span {:class (str "text-xs " muted)} says])]
           (if (empty? rows)
             [:div {:class (str "px-3 py-2 text-sm border-t border-[var(--border)] " muted)} empty-label]
             (into [:div {:class "flex flex-col"}]
                   (for [row rows]
                     ^{:key (:id row)}
                     [row-view row open text bus session-id act])))])))))

;; ---------------------------------------------------------------------------
;; In progress
;; ---------------------------------------------------------------------------

(def ^:private badge-style
  {"REVIEW" {:background "var(--accent-soft)" :color "var(--accent)"}
   "MERGED" {:background "var(--hover)" :color "var(--success)"}})

(def ^:private columns
  [["Key" "8rem"] ["What · lane" "2fr"] ["Where" "1.4fr"] ["PR" "8rem"] ["Next step" "1.4fr"] ["Updated" "10rem"]])

(def ^:private grid
  {:display "grid" :gridTemplateColumns (str/join " " (map second columns)) :columnGap "0.75rem"
   :alignItems "start"})

(defn- progress-row
  [{:keys [id key kind title lane where pull-request pull-request-url pull-request-state next-step idle updated]}]
  [:div {:class "px-3 py-2 border-t border-[var(--border)] text-sm" :style grid :data-row id}
   [:span {:class "flex items-center gap-1 min-w-0"} [mark kind] [key-link key]]
   [:span {:class "flex flex-col min-w-0"}
    [:span {:class "truncate" :title title} title]
    [:span {:class (str "text-xs truncate " muted)} (or lane "—")]]
   [:span {:class "truncate" :title where} (or where "—")]
   [:span {:class "flex items-center gap-1 min-w-0"}
    (if pull-request
      [:a {:href pull-request-url :target "_blank" :rel "noopener" :class "underline text-[var(--accent)]"} pull-request]
      [:span {:class muted} "—"])
    (when (seq pull-request-state)
      [:span {:class "text-[10px] font-semibold uppercase rounded px-1"
              :style (get badge-style pull-request-state {})}
       pull-request-state])]
   (if (seq idle)
     [:span {:class "font-semibold text-[var(--danger)]" :data-idle "true"} (str "⚑ Idle: " idle)]
     [:span {:class "min-w-0"} (or next-step "—")])
   [:span {:class (str "text-xs " muted)} (or updated "")]])

(defn widget-in-progress-list
  "The list of In progress. No gesture."
  [{:keys [items empty-label]} _bus _session-id]
  [:div {:class "flex flex-col rounded bg-[var(--panel)] border border-[var(--border)] overflow-x-auto"
         :data-in-progress "true"}
   (into [:div {:class (str "px-3 py-2 text-[11px] font-semibold uppercase tracking-wide " muted) :style grid}]
         (for [[c _] columns] ^{:key c} [:span c]))
   (if (empty? items)
     [:div {:class (str "px-3 py-2 text-sm border-t border-[var(--border)] " muted)} (or empty-label "")]
     (into [:div {:class "flex flex-col"}]
           (for [row items] ^{:key (:id row)} [progress-row row])))])
